/**
 * Client HTTP des routes du POSTE (backend/src/routes/caisse/index.js).
 *
 * - Jamais de cookie : `credentials: 'omit'`. Le poste s'authentifie par son
 *   jeton, pose explicitement dans l'en-tete X-Caisse-Token.
 * - Jamais de cache HTTP : `cache: 'no-store'` (le service worker, lui, ne
 *   touche pas aux appels de l'API, voir sw/swPolicy.js).
 * - Les chemins n'ont pas de prefixe /api : la base (VITE_API_URL) pointe sur
 *   le sous-domaine de l'API, servi par le routeur racine du backend.
 * - Le jeton n'apparait dans aucun message d'erreur.
 */

export const TOKEN_HEADER = 'X-Caisse-Token';
export const DEFAULT_TIMEOUT_MS = 10_000;
/** Catalogue : ~0,7 Mo en gzip pour le plus gros magasin (PR 1.2). */
export const CATALOG_TIMEOUT_MS = 120_000;
/** NIP : l'employe attend devant le pave ; au-dela, verification hors ligne. */
export const PIN_TIMEOUT_MS = 5_000;
const DEVICE_TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

/**
 * kind :
 *   - 'network' : pas de reponse (reseau coupe, DNS, CORS refuse) ;
 *   - 'timeout' : pas de reponse dans le delai ;
 *   - 'http'    : le serveur a repondu avec un statut d'erreur (status, code) ;
 *   - 'invalid' : le serveur a repondu 2xx, mais pas ce qui etait attendu.
 */
export class CaisseApiError extends Error {
    constructor(message, { kind, status = null, code = null } = {}) {
        super(message);
        this.name = 'CaisseApiError';
        this.kind = kind;
        this.status = status;
        this.code = code;
    }
}

async function readJson(response) {
    try {
        return await response.json();
    } catch (_error) {
        return null;
    }
}

/** Message et code d'une reponse d'erreur, quelle que soit sa forme. */
function describeError(body) {
    if (!body || typeof body !== 'object') return { message: null, code: null };
    if (body.error && typeof body.error === 'object') {
        return { message: body.error.message || null, code: body.error.code || null };
    }
    return {
        message: typeof body.error === 'string' ? body.error : (body.message || null),
        code: body.code || null,
    };
}

/**
 * @param {object} options
 * @param {string} options.baseUrl ex. https://api.amabec.ca
 * @param {typeof fetch} [options.fetchImpl]
 * @param {number} [options.timeoutMs]
 */
export function createCaisseApi({ baseUrl, fetchImpl, timeoutMs = DEFAULT_TIMEOUT_MS }) {
    const base = String(baseUrl || '').replace(/\/+$/, '');
    const doFetch = fetchImpl || ((...args) => globalThis.fetch(...args));

    /**
     * Envoie la requete et renvoie la Response brute (statut quelconque).
     * Le delai couvre aussi la lecture du corps (`readBody`), pour qu'un
     * telechargement qui s'arrete en route ne reste pas pendu.
     */
    async function send(path, { method = 'GET', body, token, headers: extraHeaders, timeout = timeoutMs, readBody } = {}) {
        const headers = { Accept: 'application/json', ...(extraHeaders || {}) };
        if (body !== undefined) headers['Content-Type'] = 'application/json';
        if (token) headers[TOKEN_HEADER] = token;

        const controller = typeof AbortController === 'function' ? new AbortController() : null;
        const timer = controller ? setTimeout(() => controller.abort(), timeout) : null;
        let response;
        try {
            response = await doFetch(`${base}${path}`, {
                method,
                headers,
                body: body === undefined ? undefined : JSON.stringify(body),
                credentials: 'omit',
                cache: 'no-store',
                mode: 'cors',
                redirect: 'error',
                signal: controller ? controller.signal : undefined,
            });
        } catch (error) {
            if (timer) clearTimeout(timer);
            if (controller && controller.signal.aborted) {
                throw new CaisseApiError('Le serveur ne repond pas', { kind: 'timeout' });
            }
            throw new CaisseApiError('Pas de connexion au serveur', { kind: 'network' });
        }
        try {
            const content = readBody ? await readBody(response) : undefined;
            return { response, content };
        } catch (error) {
            if (error instanceof CaisseApiError) throw error;
            if (controller && controller.signal.aborted) {
                throw new CaisseApiError('Le serveur ne repond pas', { kind: 'timeout' });
            }
            // Connexion coupee pendant la lecture du corps.
            throw new CaisseApiError('Telechargement interrompu', { kind: 'network', code: 'TELECHARGEMENT_INTERROMPU' });
        } finally {
            if (timer) clearTimeout(timer);
        }
    }

    function httpError(response, data) {
        const { message, code } = describeError(data);
        return new CaisseApiError(message || `Erreur ${response.status}`, {
            kind: 'http',
            status: response.status,
            code,
        });
    }

    async function request(path, options = {}) {
        const { response } = await send(path, options);
        const data = await readJson(response);
        if (!response.ok) throw httpError(response, data);
        return data;
    }

    return {
        /**
         * POST /caisse/terminals/activate
         * @returns {Promise<{ terminal_id: number, device_token: string, store: object, config: object }>}
         */
        async activateTerminal({ code, name, publicKeyJwk }) {
            const data = await request('/caisse/terminals/activate', {
                method: 'POST',
                body: { code, name, public_key_jwk: publicKeyJwk },
            });
            if (!data || !Number.isSafeInteger(data.terminal_id) || data.terminal_id <= 0
                || typeof data.device_token !== 'string' || !DEVICE_TOKEN_PATTERN.test(data.device_token)
                || !data.store || typeof data.store !== 'object') {
                throw new CaisseApiError('Reponse d\'activation inattendue', { kind: 'invalid' });
            }
            return {
                terminal_id: data.terminal_id,
                device_token: data.device_token,
                store: data.store,
                config: data.config && typeof data.config === 'object' ? data.config : {},
            };
        },

        /**
         * POST /caisse/heartbeat
         * @returns {Promise<{ server_time: string, status: string, revoked: boolean, disabled: boolean, catalog_stale: boolean }>}
         */
        async heartbeat(token, { lastSeq = null, catalogSnapshotId = null, appVersion = null } = {}) {
            const data = await request('/caisse/heartbeat', {
                method: 'POST',
                token,
                body: { last_seq: lastSeq, catalog_snapshot_id: catalogSnapshotId, app_version: appVersion },
            });
            if (!data || typeof data.revoked !== 'boolean') {
                throw new CaisseApiError('Reponse du battement de coeur inattendue', { kind: 'invalid' });
            }
            return {
                server_time: data.server_time,
                status: data.status,
                revoked: data.revoked,
                disabled: data.disabled === true,
                catalog_stale: data.catalog_stale === true,
            };
        },

        /**
         * GET /caisse/config
         * @returns {Promise<{ store: object, config: object, catalog_signing_key: { alg: string, kid: string, jwk: object } | null }>}
         */
        async getConfig(token) {
            const data = await request('/caisse/config', { token });
            if (!data || !data.store || typeof data.store !== 'object' || !data.config || typeof data.config !== 'object') {
                throw new CaisseApiError('Configuration du poste inattendue', { kind: 'invalid' });
            }
            return {
                store: data.store,
                config: data.config,
                catalog_signing_key: isSigningKey(data.catalog_signing_key) ? data.catalog_signing_key : null,
            };
        },

        /**
         * GET /caisse/catalog, avec If-None-Match quand le poste a deja un
         * instantane. 304 : { notModified: true }. 200 : le corps lu en
         * entier (un corps coupe ou illisible leve une erreur : rien n'est
         * renvoye a moitie).
         * @returns {Promise<{ notModified: true } | { notModified: false, body: object }>}
         */
        async getCatalog(token, { etag = null } = {}) {
            const { response, content } = await send('/caisse/catalog', {
                token,
                headers: etag ? { 'If-None-Match': etag } : undefined,
                timeout: CATALOG_TIMEOUT_MS,
                readBody: (res) => (res.status === 304 ? null : res.text()),
            });
            if (response.status === 304) return { notModified: true };
            let data = null;
            try {
                data = JSON.parse(content);
            } catch (_error) {
                data = null;
            }
            if (!response.ok) throw httpError(response, data);
            if (!data || typeof data !== 'object') {
                throw new CaisseApiError('Catalogue incomplet', { kind: 'invalid', code: 'TELECHARGEMENT_INCOMPLET' });
            }
            return { notModified: false, body: data };
        },

        /**
         * POST /caisse/employees/verify-pin. Le NIP part dans le corps (HTTPS)
         * et n'apparait dans aucun message d'erreur.
         * @returns {Promise<{ employee_id: number, display_name: string, rights: object }>}
         */
        async verifyPin(token, pin, { timeout = PIN_TIMEOUT_MS } = {}) {
            const data = await request('/caisse/employees/verify-pin', { method: 'POST', token, body: { pin }, timeout });
            const employee = data && data.employee;
            if (!employee || !Number.isSafeInteger(employee.employee_id) || employee.employee_id <= 0
                || typeof employee.display_name !== 'string') {
                throw new CaisseApiError('Reponse de verification du NIP inattendue', { kind: 'invalid' });
            }
            return {
                employee_id: employee.employee_id,
                display_name: employee.display_name,
                rights: employee.rights && typeof employee.rights === 'object' ? employee.rights : {},
            };
        },

        /**
         * GET /caisse/employees/roster : employes actifs du magasin, sans NIP.
         * @returns {Promise<Array<{ employee_id: number, display_name: string, has_pin: boolean, rights: object }>>}
         */
        async getRoster(token) {
            const data = await request('/caisse/employees/roster', { token });
            if (!data || !Array.isArray(data.employees)) {
                throw new CaisseApiError('Liste des employes inattendue', { kind: 'invalid' });
            }
            for (const e of data.employees) {
                if (!e || !Number.isSafeInteger(e.employee_id) || e.employee_id <= 0 || typeof e.display_name !== 'string') {
                    throw new CaisseApiError('Liste des employes inattendue', { kind: 'invalid' });
                }
            }
            return data.employees;
        },
    };
}

function isSigningKey(key) {
    return Boolean(key && typeof key === 'object'
        && key.alg === 'ES256'
        && typeof key.kid === 'string' && key.kid.length > 0
        && key.jwk && key.jwk.kty === 'EC' && key.jwk.crv === 'P-256'
        && typeof key.jwk.x === 'string' && typeof key.jwk.y === 'string');
}
